import DOMPurify from 'dompurify';

// Input validation and sanitization utilities
export class InputValidator {
  // Sanitize HTML content to prevent XSS
  static sanitizeHtml(input: string): string {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span'],
      ALLOWED_ATTR: ['class']
    });
  }

  // Validate and sanitize text input
  static sanitizeText(input: string, maxLength: number = 1000): string {
    if (typeof input !== 'string') {
      throw new Error('Input must be a string');
    }
    
    // Remove any HTML tags
    const sanitized = input.replace(/<[^>]*>/g, '');
    
    // Limit length
    if (sanitized.length > maxLength) {
      throw new Error(`Input exceeds maximum length of ${maxLength} characters`);
    }
    
    // Remove potentially dangerous characters
    return sanitized.replace(/[<>'"&]/g, '');
  }

  // Validate email format
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  // Validate search queries
  static validateSearchQuery(query: string): string {
    if (!query || typeof query !== 'string') {
      throw new Error('Search query must be a non-empty string');
    }
    
    // Remove excessive whitespace
    const trimmed = query.trim();
    
    if (trimmed.length < 2) {
      throw new Error('Search query must be at least 2 characters long');
    }
    
    if (trimmed.length > 200) {
      throw new Error('Search query must be less than 200 characters');
    }
    
    // Remove potentially dangerous characters but keep basic punctuation
    return trimmed.replace(/[<>'"&]/g, '');
  }

  // Validate numeric inputs
  static validateNumber(input: any, min: number = 0, max: number = Number.MAX_SAFE_INTEGER): number {
    const num = Number(input);
    
    if (isNaN(num)) {
      throw new Error('Input must be a valid number');
    }
    
    if (num < min || num > max) {
      throw new Error(`Number must be between ${min} and ${max}`);
    }
    
    return num;
  }

  // Validate date inputs
  static validateDate(input: any): Date {
    const date = new Date(input);
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date format');
    }
    
    // Check if date is reasonable (not too far in past/future)
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 10, 0, 1);
    const maxDate = new Date(now.getFullYear() + 10, 11, 31);
    
    if (date < minDate || date > maxDate) {
      throw new Error('Date must be within reasonable range');
    }
    
    return date;
  }

  // Validate coordinates
  static validateCoordinates(lat: any, lng: any): { lat: number; lng: number } {
    const latitude = this.validateNumber(lat, -90, 90);
    const longitude = this.validateNumber(lng, -180, 180);
    
    return { lat: latitude, lng: longitude };
  }

  // Validate place ID format (Google Places API)
  static validatePlaceId(placeId: string): string {
    if (!placeId || typeof placeId !== 'string') {
      throw new Error('Place ID must be a non-empty string');
    }
    
    // Google Place IDs typically start with specific prefixes
    const validPrefixes = ['ChIJ', 'EiY', 'GhIJ', 'EkY', 'EjY'];
    const hasValidPrefix = validPrefixes.some(prefix => placeId.startsWith(prefix));
    
    if (!hasValidPrefix || placeId.length < 10 || placeId.length > 100) {
      throw new Error('Invalid Place ID format');
    }
    
    return placeId;
  }

  // Validate URL format
  static validateUrl(url: string): string {
    if (!url || typeof url !== 'string') {
      throw new Error('URL must be a non-empty string');
    }
    
    try {
      const urlObj = new URL(url);
      
      // Only allow https and http protocols
      if (!['https:', 'http:'].includes(urlObj.protocol)) {
        throw new Error('URL must use HTTP or HTTPS protocol');
      }
      
      return url;
    } catch {
      throw new Error('Invalid URL format');
    }
  }

  // Validate file upload
  static validateFile(file: File, allowedTypes: string[], maxSize: number = 5 * 1024 * 1024): File {
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file');
    }
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }
    
    if (file.size > maxSize) {
      throw new Error(`File size exceeds maximum of ${maxSize / (1024 * 1024)}MB`);
    }
    
    return file;
  }

  // Rate limiting helper
  static createRateLimiter(maxRequests: number, windowMs: number) {
    const requests = new Map<string, number[]>();
    
    return (identifier: string): boolean => {
      const now = Date.now();
      const userRequests = requests.get(identifier) || [];
      
      // Remove old requests outside the window
      const validRequests = userRequests.filter(time => now - time < windowMs);
      
      if (validRequests.length >= maxRequests) {
        return false; // Rate limit exceeded
      }
      
      validRequests.push(now);
      requests.set(identifier, validRequests);
      
      return true; // Request allowed
    };
  }
}

// Export convenience functions
export const {
  sanitizeHtml,
  sanitizeText,
  validateEmail,
  validateSearchQuery,
  validateNumber,
  validateDate,
  validateCoordinates,
  validatePlaceId,
  validateUrl,
  validateFile,
  createRateLimiter
} = InputValidator; 